import { Box, Grid } from '@mui/material';

import { CardSkeleton } from './CardSkeleton';
import { Shimmer } from './Shimmer';

interface DashboardSkeletonProps {
  /** Admin sees 5 stat cards and two card rows; staff sees 3 and one list. */
  variant?: 'admin' | 'staff';
}

/**
 * Full-page placeholder for the Dashboard, mirroring AdminDashboard /
 * StaffDashboard down to their Grid breakpoints — stat cards go 1-up on xs,
 * 2-up on sm and 5-up from md, so the page doesn't jump when data lands.
 */
export const DashboardSkeleton = ({ variant = 'admin' }: DashboardSkeletonProps) => {
  const isAdmin = variant === 'admin';
  const statCount = isAdmin ? 5 : 3;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Title row */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Shimmer height={{ xs: 20, sm: 22, md: 24 }} width={{ xs: '60%', sm: 200 }} />
        <Shimmer height={14} width={{ xs: '80%', sm: 280 }} />
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <Shimmer height={12} width={90} />
        <Grid container spacing={2}>
          {Array.from({ length: statCount }, (_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: isAdmin ? 2.4 : 4 }}>
              <Box
                sx={(theme) => ({
                  bgcolor: 'white', border: `1px solid ${theme.palette.grey[200]}`, borderRadius: '16px',
                  px: '20px', pt: '18px', pb: '20px', boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <Shimmer height={11} width="55%" sx={{ mt: '2px' }} />
                  <Shimmer height={32} width={32} radius="8px" />
                </Box>
                <Shimmer height={{ xs: 24, sm: 28, md: 30 }} width="60%" />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {isAdmin ? (
        <>
          {/* Class-wise Collection (8) + Payment Methods (4) */}
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <CardSkeleton bodyHeight={{ xs: 200, md: 260 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CardSkeleton lines={4} />
            </Grid>
          </Grid>

          {/* Recent Admissions + Recent Installments */}
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CardSkeleton lines={5} withAvatars />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CardSkeleton lines={5} withAvatars />
            </Grid>
          </Grid>
        </>
      ) : (
        <CardSkeleton lines={6} withAvatars />
      )}
    </Box>
  );
};
